import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useWhatsNew } from '../hooks/useWhatsNew';
import { WhatsNewModalContent } from './WhatsNewModalContent';

const ALLOWED_ROUTES = ['/', '/home', '/dashboard'];

/**
 * What's New modal that shows multiple announcements in a stepper format.
 * Only displays on home/dashboard routes and when there are undismissed announcements.
 */
export function WhatsNewModal() {
  const location = useLocation();
  const {
    undismissedAnnouncements,
    currentIndex,
    totalCount,
    currentAnnouncement,
    hasNext,
    hasPrevious,
    isLoading,
    next,
    previous,
    goToIndex,
    dismissCurrent,
    dismissAll,
    trackModalOpened,
    trackStepViewed,
  } = useWhatsNew();

  const isOnAllowedRoute = ALLOWED_ROUTES.includes(location.pathname);
  const shouldShow = !isLoading && totalCount > 0 && isOnAllowedRoute;

  // Track modal opened when it becomes visible
  useEffect(() => {
    if (shouldShow) {
      trackModalOpened();
    }
  }, [shouldShow, trackModalOpened]);

  // Track step viewed when step changes
  useEffect(() => {
    if (shouldShow && currentAnnouncement) {
      trackStepViewed();
    }
  }, [shouldShow, currentAnnouncement, currentIndex, trackStepViewed]);

  const handleDismissCurrent = () => {
    dismissCurrent();
  };

  const handleDismissAll = () => {
    dismissAll();
  };

  if (!shouldShow || !currentAnnouncement) {
    return null;
  }

  return (
    <Dialog open={shouldShow} onOpenChange={() => {}} modal={false}>
      {/* Manual overlay since modal={false} can interfere with standard overlay behavior */}
      <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity" />
      
      <DialogContent
        closable={false}
        showCloseButton={false}
        className="z-50 flex max-h-[95vh] w-[95vw] max-w-5xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#0A0F0C] to-[#050807] p-0 shadow-[0_20px_100px_rgba(0,0,0,0.8)] sm:w-full"
      >
        {/* Header with close button */}
        <div className="relative flex items-center justify-between border-b border-lime/20 bg-lime/5 px-8 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-lime/20">
              <svg
                className="h-5 w-5 text-lime"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-white">What's New</h2>
          </div>
          <button
            onClick={handleDismissCurrent}
            className="rounded-lg p-2 text-white/60 transition-all hover:bg-white/10 hover:text-white"
            aria-label="Dismiss this announcement"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Main content area - scrollable on mobile */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          <WhatsNewModalContent
            announcement={currentAnnouncement}
            onDismiss={handleDismissCurrent}
          />
        </div>

        {/* Footer with navigation */}
        <div className="border-t border-lime/20 bg-gradient-to-r from-lime/5 via-lime/10 to-lime/5 px-8 py-5">
          <div className="flex items-center justify-between">
            {/* Progress section */}
            <div className="flex items-center gap-4">
              {totalCount > 1 && (
                <>
                  <div className="flex gap-2">
                    {undismissedAnnouncements.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => goToIndex(idx)}
                        className={`h-2 transition-all ${
                          idx === currentIndex
                            ? 'w-8 rounded-full bg-lime shadow-[0_0_8px_rgba(209,245,81,0.6)]'
                            : 'w-2 rounded-full bg-white/20 hover:bg-white/40'
                        }`}
                        aria-label={`Go to announcement ${idx + 1}`}
                      />
                    ))}
                  </div>
                  <div className="h-4 w-px bg-white/20" />
                </>
              )}
              <span className="text-sm font-medium text-white/70">
                <span className="text-lime">{currentIndex + 1}</span>
                <span className="mx-1.5 text-white/40">/</span>
                <span className="text-white/60">{totalCount}</span>
              </span>
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center gap-2">
              {totalCount > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={previous}
                    disabled={!hasPrevious}
                    className="border border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white disabled:opacity-30"
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={next}
                    disabled={!hasNext}
                    className="border border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white disabled:opacity-30"
                  >
                    Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDismissAll}
                className="ml-2 border-lime/30 bg-lime/10 text-lime hover:bg-lime/20 hover:text-lime"
              >
                {totalCount > 1 ? 'Dismiss All' : 'Got it'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
